import {useState, Dispatch, SetStateAction} from 'react'
import {useParams, useLocation} from 'react-router-dom'

import styles from './BorrowerProjectDrawdowns.module.scss'
import useStore from 'base/store'
import {Loader, ActionBlock, Modal, AddPlusCircleIcon} from 'common/components'
import {
  BorrowerProjectDrawdownsStats,
  ProjectDrawdownsList,
  ProjectDrawdown,
  DrawdownForm,
  useGetDrawdowns,
  DrawdownItem,
} from '..'

interface ContentProps {
  drawdowns: DrawdownItem[]
}

interface DrawdownRequestModalProps {
  setDrawdownUuid?: Dispatch<SetStateAction<string>>
}

export default function BorrowerProjectDrawdowns() {
  const params = useParams()
  const {data: drawdowns} = useGetDrawdowns(params.uuid)

  if (!drawdowns) return <Loader />

  return (
    <>
      {drawdowns.length ? (
        <div>
          <BorrowerProjectDrawdownsStats />
          <Content drawdowns={drawdowns} />
        </div>
      ) : (
        <>
          <FirstDrawdown />
          <DrawdownRequestModal />
        </>
      )}
    </>
  )
}

const FirstDrawdown = () => {
  const setDrawdownRequestOpen = useStore(
    (state) => state.setDrawdownRequestOpen
  )

  const handleOpen = () => setDrawdownRequestOpen(true)

  return (
    <ActionBlock
      icon={<AddPlusCircleIcon />}
      title="First drawdown"
      text="Fill out a form to request the first drawdown. The confirmation process will take a little time. After that, you will be able to request drawdowns faster."
      buttonText="Request a drawdown"
      onClick={handleOpen}
    />
  )
}

const DrawdownRequestModal = ({setDrawdownUuid}: DrawdownRequestModalProps) => {
  const isDrawdownRequestOpen = useStore((state) => state.isDrawdownRequestOpen)
  const setDrawdownRequestOpen = useStore(
    (state) => state.setDrawdownRequestOpen
  )

  const handleClose = () => setDrawdownRequestOpen(false)

  return (
    <Modal
      width={848}
      title="Drawdown request"
      open={isDrawdownRequestOpen}
      onCancel={handleClose}
    >
      <DrawdownForm setDrawdownUuid={setDrawdownUuid} onClose={handleClose} />
    </Modal>
  )
}

const Content = ({drawdowns}: ContentProps) => {
  const {hash} = useLocation()
  const [drawdownUuid, setDrawdownUuid] = useState(
    (hash &&
      drawdowns.find((drawdown) => drawdown.uuid === hash.slice(1))?.uuid) ||
      drawdowns[0]?.uuid
  )

  return (
    <>
      <div className={styles.content}>
        {drawdownUuid && (
          <ProjectDrawdownsList
            drawdownUuid={drawdownUuid}
            setDrawdownUuid={setDrawdownUuid}
          />
        )}
        <ProjectDrawdown key={drawdownUuid} drawdownUuid={drawdownUuid} />
      </div>
      <DrawdownRequestModal setDrawdownUuid={setDrawdownUuid} />
    </>
  )
}
